import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import './i18n/config'; // bootstraps i18next before any component renders
import { heIL, enUS } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { prefixer } from 'stylis';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { DataProvider } from './context/DataContext';
import { ImpersonationProvider } from './context/ImpersonationContext';
import { getBootDirection, getBootLocale } from './i18n/config';
import { isLiveCall, markReloadPending, reportDialerEvent } from './lib/dialerTelemetry';

// ---------------------------------------------------------------------------
// Bilingual boot
// ---------------------------------------------------------------------------
// Direction + Emotion cache + MUI locale are decided ONCE at boot, based on
// the URL (?lang=en on public /campaign/* routes flips us to LTR English).
// Everywhere else — admin, dashboard, builder chrome — we stay RTL Hebrew,
// IDENTICAL to the previous hard-coded behavior. Legacy campaigns without
// ?lang= therefore render byte-for-byte the same as before this change.
// See frontend/src/i18n/config.ts for the boot-locale rules.
// ---------------------------------------------------------------------------
// When a new version deploys, the hashed chunk files an already-open tab knows
// about can disappear. Vite fires `vite:preloadError` when a dynamic import
// fails to load — recover by reloading once (index.html is served no-cache, so
// the reload pulls the fresh chunk names). A timestamp guard prevents a reload
// loop if the import is genuinely broken rather than just stale. This catches
// the failure earlier than the React error boundary (which is the safety net).
window.addEventListener('vite:preloadError', (event) => {
  // Never reload under a live call-center call: the softphone (SIP.js over
  // WSS) lives in this tab, so a reload drops the call. Remember that a newer
  // build is waiting — the dialer shows a "refresh between calls" banner.
  if (isLiveCall()) {
    event.preventDefault();
    markReloadPending();
    reportDialerEvent('chunk_reload_suppressed', { source: 'vite:preloadError' });
    return;
  }
  let last = 0;
  try { last = Number(sessionStorage.getItem('chunk-reload-ts') || '0'); } catch { /* private mode */ }
  const now = Date.now();
  if (now - last > 8000) {
    try { sessionStorage.setItem('chunk-reload-ts', String(now)); } catch { /* private mode */ }
    event.preventDefault(); // suppress the default unhandled-rejection throw
    window.location.reload();
  }
});

const bootLocale = getBootLocale();
const bootDirection = getBootDirection();

// ---------------------------------------------------------------------------
// Dark mode bridge
// ---------------------------------------------------------------------------
// The app's dark mode is Tailwind's `darkMode: 'class'` — hooks/useDarkMode.ts
// toggles a `dark` class on <html>. MUI knew nothing about it, so every MUI
// surface (notably the two call-center DataGrids) stayed locked to the light
// palette and rendered as a white block on an otherwise dark page. Watch the
// SAME class and hand MUI a matching palette mode. Colors mirror index.css:
// --md-surface (#121212) for the page, #1A1A1A for the panels the Tailwind
// markup uses via `dark:bg-[#1A1A1A]`.
function useHtmlDarkClass(): boolean {
  const [isDark, setIsDark] = React.useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  React.useEffect(() => {
    const el = document.documentElement;
    const sync = () => setIsDark(el.classList.contains('dark'));
    // useDarkMode applies the class in an effect, i.e. AFTER our first render —
    // sync once on mount so we don't sit on a stale initial value.
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

const MuiThemeBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isDark = useHtmlDarkClass();
  const theme = React.useMemo(
    () =>
      createTheme(
        {
          direction: bootDirection,
          palette: {
            mode: isDark ? 'dark' : 'light',
            ...(isDark ? { background: { default: '#121212', paper: '#1A1A1A' } } : {}),
          },
        },
        bootDirection === 'ltr' ? enUS : heIL
      ),
    [isDark]
  );
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

// Emotion cache. We keep the SAME key/plugins as before for the RTL case
// (only `prefixer`, no stylis-plugin-rtl — the existing app relied on
// MUI's own theme.direction for RTL flipping, not on stylis). For LTR we
// use a different cache key so emotion treats the trees as distinct.
const emotionCache = createCache({
  key: bootDirection === 'ltr' ? 'mui' : 'muirtl',
  stylisPlugins: [prefixer],
});

// Apply direction at the document level so global CSS (Tailwind utilities,
// custom CSS in index.css) sees the correct value. This only takes effect
// on first paint, never changes mid-session.
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('dir', bootDirection);
  document.documentElement.setAttribute('lang', bootLocale);
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <CacheProvider value={emotionCache}>
      <MuiThemeBridge>
        <CssBaseline enableColorScheme />
        <ImpersonationProvider>
          <DataProvider>
            <App />
          </DataProvider>
        </ImpersonationProvider>
      </MuiThemeBridge>
    </CacheProvider>
  </React.StrictMode>,
)
